//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { IContentImportFromUrlRequest } from '@apis/content-library/contentLibraryApiModel';
import { addPostToLibraryByUrl } from '@apis/gatewayMediaApi';
import {
  UploadEvent,
  useUploadContext,
} from '@content-drawer/upload/UploadContextProvider';
import { MediaUploadBroadcastEvent } from '@legacy-components/common/image-uploader/image-uploader';
import { UniversalUploadBroadcastCalls } from '@legacy-components/common/image-uploader/image-uploader-constants';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { UploadQueueActions } from '@media-library/redux/upload-queue/uploadQueueSlice';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { broadcastRootScopeEvent } from '@react-bridge/wrappers/legacy-events';
import uuid from '@utils/uuid';

interface ContentImportFromUrlMutation extends IContentImportFromUrlRequest {
  id: string;
}

type MediaUrlAndDetails = IContentImportFromUrlRequest;

export const useImportFromUrl = ({ shouldBroadcastEvents = false }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { onUploadEvent } = useUploadContext();

  // TODO: PD-247443 Stop broadcasting events once we migrate upload flow to react.
  const broadcastEvent = (
    eventName: UniversalUploadBroadcastCalls | string,
    data: MediaUploadBroadcastEvent,
  ) => {
    if (shouldBroadcastEvents) {
      broadcastRootScopeEvent(eventName, data);
    }
  };

  // TODO: PD-247443 Stop broadcasting progress events once we migrate upload flow to react
  const broadcastProgress = (id: string, progress: number) => {
    broadcastEvent(`uploader:${id}:progress`, {
      loaded: progress,
      total: 100,
    });
  };

  // We need to handle the import state in 3 distinct ways to properly update the UI.
  // 1. Broadcast angular events: Legacy code for the media library upload depends on these events
  // 2. Dispatch Redux events: Some of the implementaion related to the media library upload has been moved to react so these events are required
  // 3. Calling onUploadEvent: This function manages the upload flow in the new content drawer in React.
  const handleStateOnError = (id: string) => {
    broadcastEvent(UniversalUploadBroadcastCalls.ContentFailed, {
      uuid: id,
    });
    onUploadEvent(UploadEvent.FAILED, { id: id });
    dispatch(UploadQueueActions.removeFailedItem({ itemId: id }));
  };

  const handleStateOnImportStart = (id: string, fileName?: string) => {
    broadcastEvent(UniversalUploadBroadcastCalls.QueuedUpload, id);
    dispatch(UploadQueueActions.addQueuedItem({ itemId: id }));
    onUploadEvent(UploadEvent.STARTED, {
      id,
      urlImport: {
        fileName,
      },
    });
  };

  const handleStateOnImportComplete = (id: string, cuid?: Cuid) => {
    broadcastProgress(id, 100);
    onUploadEvent(UploadEvent.IN_PROGRESS, { id, progress: 1 });

    if (cuid) {
      const { itemId } = Cuid.parse(cuid);
      broadcastEvent(UniversalUploadBroadcastCalls.ContentUploaded, {
        id: itemId,
        uuid: id,
      });
      onUploadEvent(UploadEvent.COMPLETED, { id });
    }
    dispatch(UploadQueueActions.removeUploadedItem({ itemId: id }));
  };

  const uploadMutation = useMutation({
    mutationFn: async (request: ContentImportFromUrlMutation) => {
      return addPostToLibraryByUrl(request);
    },
    onError: (error, request) => {
      const fileName = request.metadata?.name;

      dispatch(
        toastNotificationsActions.addToastNotification({
          message: `The file${fileName ? ` ${fileName}` : ''} could not be imported. Please try again or contact Bazaarvoice Support.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
          title: 'Import Failed!',
          type: ToastType.ERROR,
        }),
      );

      handleStateOnError(request.id);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaFilters,
          QueryKeyConstants.MediaFiltersMediaItems,
        ],
      });
    },
  });

  // we show a progress bar animation to the user that does not represent actual progress.
  // this is because we currently do not have any mechanism to get the real progress
  // we show progress bar to keep the UX same between manual upload and import from dropbox/url's
  const showProgressAnimation = (id: string) => {
    let progress = 0;
    const progressInterval = setInterval(() => {
      // the increment in progress reduces as the progress increases.
      progress += 10 * Math.exp(-0.05 * progress);
      if (progress > 90) {
        // stop the progress at 90%
        progress = 90;
        clearInterval(progressInterval);
      }
      broadcastProgress(id, progress);
      onUploadEvent(UploadEvent.IN_PROGRESS, { id, progress: progress / 100 });
    }, 1000);

    return progressInterval;
  };

  const importMediaItems = async (mediaItems: MediaUrlAndDetails[]) => {
    const toastId = uuid();

    dispatch(
      toastNotificationsActions.addToastNotification({
        id: toastId,
        message: 'Importing from Dropbox',
        options: {
          timeOut: 60000,
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
        title: 'In Progress',
        type: ToastType.INFO,
      }),
    );

    await Promise.all(
      mediaItems.map(async (item) => {
        const id = uuid();

        handleStateOnImportStart(id, item.metadata?.name);

        const progressInterval = showProgressAnimation(id);

        try {
          const response = await uploadMutation.mutateAsync({
            id,
            ...item,
          });
          const cuid = response.cuids[0];

          handleStateOnImportComplete(id, cuid);
        } finally {
          clearInterval(progressInterval);
        }
      }),
    );

    dispatch(toastNotificationsActions.closeToastNotification(toastId));
  };

  return { importMediaItems };
};
